import { useEventListener } from "@vueuse/core";
import { ref, watch } from "vue";

/**
 * The window event an opening tooltip dispatches, with its tooltip id in `detail`.
 */
export const TOOLTIP_OPEN_EVENT = "sp-tooltip-open";

/**
 * Keeps one tooltip on screen at a time: when a tooltip opens, every other tooltip leaves at once,
 * without its fade. That covers a tooltip that is still open, one that is already fading out, and one
 * that closed in the same tick and has not started its fade yet (focus moving straight from one
 * tooltip to another).
 *
 * Bind `fadesOut` to the popup transition's `css`, and `rememberFadingPopup` / `forgetFadingPopup` to
 * its `before-leave` / `after-leave` hooks.
 *
 * @param {Object} options
 * @param {string} options.tooltipId - Unique id of this tooltip.
 * @param {import("vue").Ref<boolean>} options.isVisible - Whether this tooltip is open.
 * @param {() => void} options.close - Closes this tooltip immediately.
 * @returns {{
 *   fadesOut: import("vue").Ref<boolean>,
 *   rememberFadingPopup: (popupElement: HTMLElement) => void,
 *   forgetFadingPopup: () => void,
 * }}
 */
export function useExclusiveTooltip({ tooltipId, isVisible, close }) {
  const fadesOut = ref(true);
  let fadingPopup = null;

  function rememberFadingPopup(popupElement) {
    fadingPopup = popupElement;
  }

  function forgetFadingPopup() {
    fadingPopup = null;
  }

  function announceOpen(visible) {
    if (visible) {
      fadesOut.value = true;
      window.dispatchEvent(new CustomEvent(TOOLTIP_OPEN_EVENT, { detail: tooltipId }));
    }
  }

  function leaveForOtherTooltip({ detail: openedTooltipId }) {
    if (openedTooltipId === tooltipId) {
      return;
    }

    fadesOut.value = false;

    if (fadingPopup) {
      fadingPopup.style.visibility = "hidden";
    }

    if (isVisible.value) {
      close();
    }
  }

  watch(isVisible, announceOpen);
  useEventListener(window, TOOLTIP_OPEN_EVENT, leaveForOtherTooltip);

  return { fadesOut, rememberFadingPopup, forgetFadingPopup };
}
